<template>
    <div class="list-view">
        <left-nav @click="(path) => $router.push(`/library?path=${encodeURIComponent(path)}`)" />
        <div class="detail" v-show="loaded">
            <div class="actions">
                <div class="ls">
                    <h2 class="title">
                        {{ currentFolderName }}
                    </h2>
                </div>
                <div class="rs">
                    <search-bar ref="searchBar" :initValue="terms" :searchDelay="200" @change="search" />
                    <template v-if="!currentFolder || !['Drafts', 'Public'].includes(currentFolder.name)">
                        <user-chooser
                            noSelectionText="Any Box"
                            :onlyShowAuthorized="true"
                            :value="assignedToFilterValue"
                            :fullWidth="true"
                            :hideDisabled="false"
                            @change="onAssignedToFilterChange"
                        />
                        <status-chooser
                            noSelectionText="Any status"
                            :value="filter.statuses"
                            @change="
                                (statuses) =>
                                    updateFilterProp({
                                        name: 'statuses',
                                        value: statuses,
                                    })
                            "
                            :extraItems="
                                assignedToFilterValue
                                    ? [{ name: `Pending with ${assignedToFilterName}`, value: 'Pending with' }]
                                    : []
                            "
                            :multiple="true"
                        />
                        <div id="filter" class="clickable">
                            <span class="icon far fa-filter"></span>
                        </div>
                        <elem-dropdown triggerSelector="#filter" :closeOnClick="false">
                            <expanded-filter />
                        </elem-dropdown>
                    </template>
                    <submit-button :click="loadChanges">
                        <i class="far fa-sync-alt"></i>
                    </submit-button>
                </div>
            </div>
            <div class="main-list">
                <div class="top">
                    <div class="ls">
                        <input
                            v-if="items.length"
                            type="checkbox"
                            :checked="selectedItemIds.length && selectedItemIds.length >= items.length"
                            :indeterminate.prop="selectedItemIds.length && selectedItemIds.length < items.length"
                            @change="(e) => toggleCheckAll(e.target.checked)"
                        />
                        <div v-if="selectedItemIds.length" class="multi-actions">
                            <template v-if="allCheckedItemsActive">
                                <span
                                    class="far fa-envelope clickable toggle-read"
                                    @click="setRead(false)"
                                    title="Mark unread"
                                    v-if="selectionIsExpanded || allCheckedItemsRead"
                                ></span>
                                <span
                                    class="far fa-envelope-open-text clickable toggle-read"
                                    @click="setRead(true)"
                                    title="Mark read"
                                    v-if="selectionIsExpanded || !allCheckedItemsRead"
                                ></span>
                            </template>
                            <div v-if="currentFolder && currentFolder.name === 'Public'">
                                <span
                                    title="Delete public links"
                                    class="far fa-trash clickable"
                                    @click="$store.dispatch('boxes/deleteOrArchive')"
                                ></span>
                            </div>
                            <div v-else-if="currentFolder && currentFolder.name === 'Drafts'">
                                <span
                                    title="Delete items"
                                    class="far fa-trash clickable"
                                    @click="$store.dispatch('boxes/deleteDrafts')"
                                ></span>
                            </div>
                            <template v-else>
                                <span
                                    title="Archive items"
                                    class="far fa-archive clickable"
                                    @click="$store.dispatch('boxes/archiveSelected', true)"
                                    v-if="selectionIsExpanded || !allCheckedItemsArchived"
                                ></span>
                                <span
                                    title="Unarchive items"
                                    class="far fa-inbox clickable"
                                    @click="$store.dispatch('boxes/archiveSelected', false)"
                                    v-if="selectionIsExpanded || allCheckedItemsArchived"
                                ></span>
                            </template>
                            <div 
                                v-if="
                                    currentFolder &&
                                    currentFolder.name === 'Archive' &&
                                    user.hasRole(org.settings.deletePkgMinRole)
                                "
                            >
                                <span
                                    title="Delete items"
                                    class="far fa-trash clickable"
                                    @click="$store.dispatch('boxes/deletePackages')"
                                ></span>
                            </div>
                            <user-chooser
                                v-if="!currentFolder || currentFolder.name !== 'Drafts'"
                                placeholder="Assign To"
                                noSelectionText="Unassigned"
                                :value="assignedToUser"
                                :fullWidth="true"
                                @change="reassignSelected"
                            />
                        </div>
                    </div>
                    <div class="rs">
                        <div id="sort" class="clickable">
                            {{ activeSortGroup.name }}
                            <span
                                :class="`icon far fa-sort-${
                                    activeSortGroup.col === 'updated_at' ? 'numeric' : 'alpha'
                                }-up`"
                                v-if="sort.dir === 'asc'"
                            ></span>
                            <span
                                :class="`icon far fa-sort-${
                                    activeSortGroup.col === 'updated_at' ? 'numeric' : 'alpha'
                                }-down`"
                                v-if="sort.dir === 'desc'"
                            ></span>
                        </div>
                        <elem-dropdown triggerSelector="#sort">
                            <div id="expanded-sort">
                                <sort-group
                                    v-for="(group, index) in sortGroups"
                                    :key="index"
                                    :name="group.name"
                                    :col="group.col"
                                />
                            </div>
                        </elem-dropdown>

                        <button class="primary new-package" @click="$router.push({ name: 'FormLibrary' })">
                            New Package
                        </button>
                    </div>
                </div>
                <div class="items-wrapper">
                    <div
                        class="select-all"
                        v-if="
                            selectedItemIds.length &&
                            selectedItemIds.length >= items.length &&
                            totalItems > items.length
                        "
                    >
                        <div v-if="selectionIsExpanded">
                            All {{ totalItems }} in this box are selected.
                            <span class="modify-selection" @click="setSelected([])">Clear Selection</span>
                        </div>
                        <div v-else>
                            All {{ selectedItemIds.length }} items on this page are selected.
                            <span class="modify-selection" @click="$store.dispatch('boxes/selectAllItems')"
                                >Select all {{ totalItems }} items in this box</span
                            >
                        </div>
                    </div>
                    <div v-if="isLoading" class="no-items"><loading-spinner /> Loading...</div>
                    <div class="items" v-else-if="items.length">
                        <package-list-item
                            v-for="(item, index) in items"
                            :key="item.id"
                            :pkg="item"
                            :index="index"
                            :box="currentBox.info"
                        />
                    </div>
                    <div v-else class="no-items">No items</div>
                </div>
                <div class="bottom" v-if="totalItems">
                    <div class="ls">
                        <span
                            >{{ itemFromNum }}-{{ itemToNum }} of {{ totalItems }}
                            {{ totalItems === 1 ? ' Item' : ' Items' }}</span
                        >
                    </div>
                    <div class="rs">
                        <div>
                            <span>Show</span>
                            <select
                                class="items-per-page"
                                :value="itemsPerPage"
                                @change="(e) => setItemsPerPage(e.target.value)"
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                            </select>
                            <span>items</span>
                        </div>

                        <div class="separator"></div>
                        <div>
                            <span
                                v-if="numPages > 2"
                                class="first far fa-chevron-double-left clickable"
                                @click="setPageIndex(0)"
                                :class="{ disabled: pageIndex === 0 }"
                            ></span>
                            <span
                                class="prev far fa-chevron-left clickable"
                                @click="setPageIndex(pageIndex - 1)"
                                :class="{ disabled: pageIndex === 0 }"
                            ></span>
                            <div class="ellipsis" style="margin-left: 0.15em" v-if="selectablePageIndexes[0] !== 0">
                                &hellip;
                            </div>
                            <span
                                class="page-num clickable"
                                v-for="index in selectablePageIndexes"
                                :key="index"
                                :class="{ active: index === pageIndex }"
                                @click="setPageIndex(index)"
                                >{{ index + 1 }}</span
                            >
                            <div
                                class="ellipsis"
                                style="margin-right: 0.15em"
                                v-if="selectablePageIndexes[selectablePageIndexes.length - 1] !== numPages - 1"
                            >
                                &hellip;
                            </div>
                            <span
                                class="next far fa-chevron-right clickable"
                                @click="setPageIndex(pageIndex + 1)"
                                :class="{ disabled: pageIndex === numPages - 1 }"
                            ></span>
                            <span
                                v-if="numPages > 2"
                                class="last far fa-chevron-double-right clickable"
                                @click="setPageIndex(numPages - 1)"
                                :class="{ disabled: pageIndex === numPages - 1 }"
                            ></span>
                        </div>
                        <div class="separator"></div>
                        <div>
                            <span>Go to page</span>
                            <select :value="pageIndex" @change="(e) => changePage(e.target.value)">
                                <option v-for="(num, index) in numPages" :value="index" :key="index">
                                    {{ num }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import { mapGetters, mapMutations } from 'vuex';
    import { capitalize } from '../utils/strings';
    import PackageListItem from '../components/boxes/PackageListItem';
    import ExpandedFilter from '../components/boxes/ExpandedFilter';
    import UserChooser from '../components/UserChooser';
    import SortGroup from '../components/boxes/SortGroup';
    import StatusChooser from '../components/StatusChooser';
    const isEqual = require('lodash/isEqual');
    const cloneDeep = require('lodash/cloneDeep');

    export default {
        name: 'Boxes',
        data() {
            return {
                loaded: false,
                pauseUpdates: false,
                isLoading: false,
                skipUrlUpdate: false,
                lastRequestId: 0,
            };
        },
        components: { PackageListItem, ExpandedFilter, SortGroup, UserChooser, StatusChooser },
        computed: {
            ...mapGetters({
                selectedItemIds: 'boxes/selectedItemIds',
                user: 'user/user',
                users: 'org/users',
                sections: 'boxes/sections',
                itemsPerPage: 'boxes/itemsPerPage',
                pageIndex: 'boxes/pageIndex',
                items: 'boxes/items',
                totalItems: 'boxes/totalItems',
                availableBoxes: 'boxes/availableBoxes',
                sort: 'boxes/sort',
                sortGroups: 'boxes/sortGroups',
                activeSortGroup: 'boxes/activeSortGroup',
                filter: 'boxes/filter',
                terms: 'boxes/terms',
                boxById: 'boxes/boxById',
                parseBoxString: 'boxes/parseBoxString',
                org: 'org/org',
                appName: 'base/appName',
            }),
            currentBox() {
                return this.availableBoxes[this.$route.params.boxIndex] || false;
            },
            currentFolder() {
                if (!this.currentBox) return false;

                let filterName = this.$route.params.filter.toLowerCase();
                let namedFolder = this.currentBox.folders.find(
                    (folder) => folder.name.toLowerCase() === filterName
                );
                if (namedFolder) return namedFolder;

                let filterMatchedFolder = this.currentBox.folders.find((folder) => isEqual(folder.filter, this.filter));
                return filterMatchedFolder;
            },
            currentFolderName() {
                if (!this.currentFolder) return 'Custom Box';

                if (this.currentFolder.name === 'Inbox') {
                    return this.currentBox.info.name;
                } else if (this.currentFolder.name === 'Public') {
                    return 'Public Links';
                }

                return this.currentFolder.name;
            },
            numPages() {
                return Math.ceil(this.totalItems / this.itemsPerPage);
            },
            start() {
                return this.pageIndex * this.itemsPerPage;
            },
            end() {
                if (!this.items.length) return 0;

                return Math.min(this.totalItems.length - 1 || this.start + this.itemsPerPage - 1);
            },
            allCheckedItemsArchived() {
                return this.selectedItemIds.every((id) => {
                    let pkg = this.items.find((pkg) => pkg.id === id);
                    return pkg && pkg.is_archived;
                });
            },
            allCheckedItemsActive() {
                return this.selectedItemIds.every((id) => {
                    let pkg = this.items.find((pkg) => pkg.id === id);
                    return pkg && !pkg.is_archived && pkg.status === 'Active';
                });
            },
            allCheckedItemsRead() {
                return this.selectedItemIds.every((id) => {
                    let pkg = this.items.find((pkg) => pkg.id === id);
                    return pkg && pkg.is_read;
                });
            },
            assignedToUser() {
                let assignee;

                for (let id of this.selectedItemIds) {
                    let pkg = this.items.find((item) => item.id === id);

                    // if expanded selection, we only have ids for some packages, not data
                    if (!pkg) return '-1';

                    let assigneeStr = pkg.assigned_to_user_id;
                    if (pkg.assigned_to_box_id && !assigneeStr) assigneeStr = 'box_' + pkg.assigned_to_box_id;

                    if (assignee === undefined) {
                        assignee = assigneeStr;
                    } else if (assigneeStr !== assignee) {
                        return '-1';
                    }
                }

                return assignee;
            },
            assignedToFilterValue() {
                return this.filter.assigned_to_box_id
                    ? 'box_' + this.filter.assigned_to_box_id
                    : this.filter.assigned_to_user_id;
            },
            assignedToFilterName() {
                return this.filter.assigned_to_box_id
                    ? this.boxById(this.filter.assigned_to_box_id).name
                    : this.filter.assigned_to_user_id
                        ? this.users[this.filter.assigned_to_user_id].name
                        : '';
            },
            selectionIsExpanded() {
                return this.selectedItemIds.length > this.items.length;
            },
            selectablePageIndexes() {
                let selectables = [];
                for (let i = 0; i < this.numPages; i++) {
                    if (Math.abs(this.pageIndex - i) <= 1) {
                        selectables.push(i);
                    } else if (
                        (this.pageIndex === this.numPages - 1 || this.pageIndex === 0) &&
                        Math.abs(this.pageIndex - i) <= 2
                    ) {
                        selectables.push(i);
                    }
                }

                return selectables;
            },
            itemFromNum() {
                return this.pageIndex * this.itemsPerPage + 1;
            },
            itemToNum() {
                return Math.min(this.pageIndex * this.itemsPerPage + this.itemsPerPage, this.totalItems);
            },
        },
        methods: {
            ...mapMutations({
                setPageIndex: 'boxes/setPageIndex',
                setItemsPerPage: 'boxes/setItemsPerPage',
                setSelected: 'boxes/setSelected',
                setFilter: 'boxes/setFilter',
                setSort: 'boxes/setSort',
                setTerms: 'boxes/setTerms',
                updateFilterProp: 'boxes/updateFilterProp',
            }),
            changePage(index) {
                if (index >= 0 && index < this.numPages) {
                    this.setPageIndex(index);
                }

                this.setSelected([]);
            },
            toggleCheckAll(checked) {
                if (!checked) {
                    this.setSelected([]);
                } else if (checked && this.selectedItemIds.length !== this.items.length) {
                    let newIndexes = [];
                    for (let i = 0; i < this.items.length; i++) newIndexes.push(this.items[i].id);
                    this.setSelected(newIndexes);
                }
            },
            addWatch() {
                ['pageIndex', 'filter', 'terms', 'itemsPerPage', 'sort'].forEach((propName) => {
                    this.$watch(propName, watcherFn, { deep: propName === 'filter' });

                    function watcherFn() {
                        if (!this.loaded) return;

                        if (propName !== 'pageIndex') {
                            this.setPageIndex(0);
                        }

                        this.setSelected([]);
                        this.updateLocalStorage();
                        if (propName === 'filter') {
                            this.updateUrlParams();
                        }

                        this.loadChanges();
                    }
                });
            },
            async loadChanges() {
                this.isLoading = true;
                let reqId = await this.$store.dispatch('boxes/load', ++this.lastRequestId);
                if (reqId === this.lastRequestId) this.isLoading = false;
            },
            updateLocalStorage() {
                localStorage['boxes/itemsPerPage'] = this.itemsPerPage;
            },
            updateFromLocalStorage() {
                let itemsPerPage = localStorage['boxes/itemsPerPage'];
                if (parseInt(itemsPerPage)) {
                    this.setItemsPerPage(parseInt(itemsPerPage));
                }
                let sort = localStorage['boxes/sort'];
                if (sort) {
                    this.setSort(JSON.parse(sort));
                }
            },
            updateUrlParams() {
                if (this.skipUrlUpdate) {
                    this.skipUrlUpdate = false;
                    return;
                }

                let filterAssignedTo = this.filter.assigned_to_user_id || this.filter.assigned_to_box_id;
                let assignedToBoxIndex = this.availableBoxes.findIndex((box) => box.info.id === filterAssignedTo);
                let currentFolder;
                if (assignedToBoxIndex !== -1) {
                    let currentBox = this.availableBoxes[assignedToBoxIndex];
                    currentFolder = currentBox.folders.find((folder) => isEqual(folder.filter, this.filter));
                }
                let newFilterString;
                if (currentFolder) {
                    newFilterString = currentFolder.name.toLowerCase();
                } else {
                    newFilterString = encodeURIComponent(btoa(JSON.stringify(this.filter)));
                }

                let params = {
                    boxIndex: assignedToBoxIndex,
                    filter: newFilterString,
                };

                if (isEqual(params, this.$route.params)) return;

                this.pauseUpdates = true;
                this.$router.replace({ name: 'Boxes', params });
            },
            async updateFromUrlParams(params = this.$route.params) {
                this.skipUrlUpdate = true;
                await this.$nextTick();
                let filterString = params.filter || 'inbox';

                let currentFolder;
                if (this.currentBox) {
                    currentFolder = this.currentBox.folders.find((box) => {
                        return box.name.toLowerCase() === filterString.toLowerCase();
                    });
                }

                if (currentFolder) {
                    this.setFilter(cloneDeep(currentFolder.filter));
                } else if (filterString) {
                    // load arbitrary filter, etc. from param string
                    try {
                        let filter = JSON.parse(atob(decodeURIComponent(filterString)));
                        this.setFilter(filter);
                    } catch (e) {
                        this.$router.replace({ name: 'Boxes' });
                    }
                } else {
                    this.$store.dispatch('boxes/initFilter');
                }
            },
            async setRead(markRead) {
                let vm = this;
                let doChange = true;
                let markString = `${this.selectedItemIds.length} item${this.selectedItemIds.length > 1 ? 's' : ''} ${
                    markRead ? 'Read' : 'Unread'
                }`;

                function currentUserIsTheAssignedUser() {
                    return vm.selectedItemIds.every((id) => {
                        let pkg = vm.items.find((pkg) => pkg.id === id);
                        return pkg.assigned_to_user_id === vm.user.id;
                    });
                }

                if (this.selectionIsExpanded || !currentUserIsTheAssignedUser()) {
                    doChange = await this.$store.dispatch('dialog/set', {
                        type: 'confirm',
                        header: `Mark ${markString}?`,
                        critical: true,
                        body: 'This will affect all users.',
                    });
                }

                if (doChange) {
                    await this.$store.dispatch('boxes/updateSelected', {
                        isRead: markRead,
                    });

                    this.$store.dispatch('notification/success', `Marked ${markString}`);
                }
            },
            async reassignSelected(assigneeStr) {
                let change = await this.$store.dispatch('package/reassignConfirm', assigneeStr);
                let assignMessage = '';
                if (typeof change === 'string') {
                    assignMessage = change;
                    change = true;
                }

                if (!change) return;
                let reassignString = `${this.selectedItemIds.length} item${
                    this.selectedItemIds.length > 1 ? 's' : ''
                } reassigned`;

                let assigneeDetails = this.parseBoxString(assigneeStr, 'camel');
                if (assigneeDetails) {
                    await this.$store.dispatch('boxes/updateSelected', {
                        [`assignedTo${capitalize(assigneeDetails.type)}Id`]: assigneeDetails.id,
                        [`assignedTo${capitalize(assigneeDetails.otherType)}Id`]: '',
                        assignMessage,
                    });
                } else {
                    await this.$store.dispatch('boxes/updateSelected', {
                        assignedToUserId: '',
                        assignedToBoxId: '',
                    });
                }

                await this.$store.dispatch('notification/success', `${reassignString}`);
                this.$store.dispatch('boxes/loadCounts');
            },
            onAssignedToFilterChange(assigneeStr) {
                if (!assigneeStr) {
                    if (this.filter.assigned_to_user_id) {
                        this.updateFilterProp({ name: 'assigned_to_user_id', value: '' });
                    } else if (this.filter.assigned_to_box_id) {
                        this.updateFilterProp({ name: 'assigned_to_box_id', value: '' });
                    }
                    return;
                }

                let assigneeDetails = this.parseBoxString(assigneeStr);
                this.updateFilterProp({ name: `assigned_to_${assigneeDetails.type}_id`, value: assigneeDetails.id });
            },
            search(terms) {
                if (terms.trim() !== this.terms.trim()) this.setTerms(terms);
            },
        },
        directives: {
            indeterminate: function (el, binding) {
                el.indeterminate = Boolean(binding.value);
            },
        },
        async mounted() {
            this.$store.dispatch('boxes/initFilter');
            await this.updateFromUrlParams();
            this.updateFromLocalStorage();
            this.addWatch();
            await this.$store.dispatch('boxes/load');
            this.$loading(false);
            this.loaded = true;
            document.title = this.currentFolderName + ' - ' + this.appName;
        },
        beforeRouteUpdate(to, from, next) {
            if (this.pauseUpdates) {
                this.pauseUpdates = false;
            } else {
                this.setTerms('');
                this.$refs.searchBar.searchParams = '';
                this.updateFromUrlParams(to.params);
            }

            this.$store.dispatch('boxes/loadCounts');
            next();
        },
        beforeRouteLeave(to, from, next) {
            this.$loading();
            next();
        },
        watch: {
            currentFolderName(title) {
                document.title = title + ' - ' + this.appName;
            },
        },
    };
</script>

<style lang="scss" scoped>
    $height-actions: 50px;

    .list-view {
        display: flex;
        height: 100%;
        background: $color-white;
    }

    .detail {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        height: 100%;
        padding-top: 0.5em;
        width: calc(100% - 250px);

        .actions,
        .main-list .top,
        .main-list .bottom {
            padding-left: 1em;
            padding-right: 1em;
        }
    }

    .actions {
        height: $height-actions;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .ls,
        .rs {
            display: flex;
            align-items: center;

            .title {
                margin: 0;
            }

            > :not(:last-child) {
                margin-right: 1em;
            }

            .user-chooser {
                width: 175px;
            }

            .status-chooser {
                width: 175px;
            }

            ::v-deep .selected-user,
            ::v-deep .selected-status {
                height: 30px;
            }
        }
    }

    .main-list {
        display: flex;
        flex-direction: column;
        height: calc(100% - #{$height-actions});

        .top {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-top: 0.5em;
            padding-bottom: 0.5em;

            input[type='checkbox'] {
                margin: 0;
            }

            .ls,
            .rs {
                display: flex;
                align-items: center;
            }

            input[type='checkbox'] {
                margin-left: calc(0.5em + 1px);
            }

            .multi-actions {
                display: flex;
                align-items: center;

                > *:not(:first-child) {
                    margin-left: 0.5em;
                }

                > *:first-child {
                    margin-left: 1em;
                }

                ::v-deep .selected-user {
                    height: 25px;
                }
            }

            .new-package {
                margin-left: 1em;
            }
        }

        .items-wrapper {
            display: flex;
            flex-direction: column;
            overflow: auto;
            flex-grow: 1;
            box-shadow: inset 0px 1px 5px 0px rgba(0, 0, 0, 0.1)
        }

        .select-all {
            text-align: center;
            padding: 1em 0 0.5em;

            .modify-selection {
                @extend a;
            }
        }

        .items {
            width: 100%;
        }

        .no-items {
            text-align: center;
            padding: 2em;
            width: 100%;
            @extend .info-label;

            .loading-spinner {
                display: inline-block;
                margin-right: 10px;
                position: relative;
                top: 3px;
            }
        }

        .bottom {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding-top: 0.5em;
            padding-bottom: 0.5em;
            box-shadow: 1px -1px 2px 0px rgba(0, 0, 0, 0.1);
            @include border-radius-smaller;

            .ls,
            .rs {
                display: flex;
                align-items: center;
                position: relative;

                > * {
                    display: flex;
                    align-items: center;
                    margin-left: 0.35em;
                    margin-right: 0.35em;
                }

                * {
                    user-select: none;
                }
            }

            .separator:before {
                content: '';
                position: absolute;
                top: 2.5%;
                height: 95%;
                width: 1px;
                background: $color-border;
            }

            select {
                height: 25px;
                padding: 0 1.75em 0 0.5em;
                margin: 0.35em;
            }

            input {
                text-align: center;
                padding: 0;
                height: 25px;
                margin: 0.35em;
            }

            .first,
            .last,
            .next,
            .prev {
                padding-left: 0.25em;
                padding-right: 0.25em;
                min-width: 20px;

                &.disabled {
                    color: $color-inactive-text;
                    pointer-events: none;
                }
            }

            .page-num {
                margin-right: 0.15em;
                margin-left: 0.15em;

                &.active {
                    background: $color-disabled-bg;
                    border-color: $color-border;
                }
            }
        }
    }

    .clickable {
        @include clickable;
    }

    #sort,
    #filter {
        .icon {
            font-size: larger;
        }
    }

    #sort .icon {
        margin-left: 0.35em;
    }

    .status {
        width: 100%;
    }
</style>